import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  setupAdminRequestSchema,
  setupAdminResultSchema,
  setupStatusSchema,
  type SetupAdminRequest,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';

/**
 * First-run setup (GET /api/setup/status, POST /api/setup/admin; both public). The server decides
 * everything: whether setup is still possible, and whether the setup secret is right. The secret
 * typed by the operator is sent once with the form and never stored or kept in memory afterwards.
 */
export const SETUP_STATUS_KEY = ['setup-status'] as const;

export function useSetupStatus(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: SETUP_STATUS_KEY,
    enabled: options.enabled ?? true,
    queryFn: () => apiRequest('/setup/status', { schema: setupStatusSchema }),
    staleTime: 0,
    retry: false,
  });
}

export function useCreateFirstAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetupAdminRequest) =>
      apiRequest('/setup/admin', {
        method: 'POST',
        body: setupAdminRequestSchema.parse(input),
        schema: setupAdminResultSchema,
      }),
    // Created: setup is now closed (the server says the same from now on). Refused: re-read the
    // status, in case setup was completed or disabled meanwhile.
    onSuccess: () =>
      queryClient.setQueryData(SETUP_STATUS_KEY, { setupRequired: false, setupAvailable: false }),
    onError: () => queryClient.invalidateQueries({ queryKey: SETUP_STATUS_KEY }),
  });
}
